<template>
    <LoadingPanel
        :headline="t('common.masterData.title')"
        :is-pending="isPending"
        :is-error="isError"
    >
        <div
            v-if="data"
            class="master-data"
        >
            <div class="master-data__group">
                <MasterDataItem
                    v-if="hasValue(data.decommissioningDate)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.decommissioned')"
                    :value="format(new Date(data.decommissioningDate as string), DATE_FORMAT)"
                />
                <MasterDataItem
                    v-if="hasValue(data.plantGroupName)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.plantGroup')"
                    :value="data.plantGroupName"
                />
                <MasterDataItem
                    v-if="hasValue(data.plantName)"
                    :label="
                        data.isReserve
                            ? t('transformers.conditionStatus.content.masterData.labels.storagePlant')
                            : t('transformers.conditionStatus.content.masterData.labels.plant')
                    "
                    :value="data.plantName"
                />
                <MasterDataItem
                    v-if="hasValue(data.transformerName)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.transformerName')"
                    :value="data.transformerName"
                />
                <MasterDataItem
                    v-if="hasValue(data.transformerSid)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.transformerId')"
                    :value="data.transformerSid"
                />
                <MasterDataItem
                    v-if="hasValue(data.technicalLocation)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.technicalLocation')"
                    :value="data.technicalLocation"
                />
                <MasterDataItem
                    v-if="hasValue(data.manufacturer)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.manufacturer')"
                    :value="data.manufacturer"
                />
                <MasterDataItem
                    v-if="hasValue(data.transformerType)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.constructionType')"
                    :value="data.transformerType"
                />
                <MasterDataItem
                    v-if="hasValue(data.constructionYear)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.constructionYear')"
                    :value="data.constructionYear"
                />
            </div>
            <div class="master-data__group">
                <MasterDataItem
                    v-if="hasValue(data.nominalPower)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.nominalPower')"
                    :value="data.nominalPower"
                />
                <MasterDataItem
                    v-if="hasValue(data.primaryVoltageOs)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.primaryVoltage')"
                    :value="data.primaryVoltageOs"
                />
                <MasterDataItem
                    v-if="hasValue(data.secondaryVoltageOs)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.secondaryVoltage')"
                    :value="data.secondaryVoltageOs"
                />
                <MasterDataItem
                    v-if="hasValue(data.coolingSystem)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.coolingSystem')"
                    :value="data.coolingSystem"
                />
                <MasterDataItem
                    v-if="hasValue(data.oilWeight)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.oilWeight')"
                    :value="data.oilWeight"
                />
                <MasterDataItem
                    v-if="hasValue(data.totalWeight)"
                    :label="t('transformers.conditionStatus.content.masterData.labels.totalWeight')"
                    :value="data.totalWeight"
                />
            </div>
        </div>
    </LoadingPanel>
</template>

<script setup lang="ts">
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import { useI18n } from 'vue-i18n'
import { useTransformerDetailsQuery } from '@/api/queries/transformers/useTransformerDetailsQuery'
import { toRefs } from 'vue'
import MasterDataItem from '@/components/master-data/MasterDataItem.vue'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import { hasValue } from '@/utils/master-data-utils'

const props = defineProps<{
    transformerId: string
}>()

const { transformerId } = toRefs(props)

const { t } = useI18n()
const { data, isPending, isError } = useTransformerDetailsQuery(transformerId)
</script>

<style scoped lang="scss">
.master-data {
    display: flex;
    align-items: flex-start;
    gap: $spacing-l;

    &__group {
        display: grid;
        grid-template-columns: 1fr 1fr;

        row-gap: $spacing-2xs;
        column-gap: $spacing-xs;
    }
}
</style>
